// The observer as a body of its own among the bodies (gravity mode): a position and a velocity in the star's frame, moved by gravity and by its thrusters. No three.js here.
//
// The bodies are the server's, on their circles (bodies.ts); the observer is a test body that does not pull on them. Each frame it is advanced by the number of ticks the simulation has
// advanced (never more: there is no time warp, a faster sky is a higher tick rate), in as many steps as the nearest pull needs (a fraction of the time that body's pull takes to turn a
// velocity through a radian), kick-drift-kick, which holds an orbit's energy over many turns. The thrust is an acceleration held over the frame.
import { add, sub, scale, norm, len, dot } from './bodies';
import type { Bodies, V3 } from './bodies';
import { pullAt } from './gravity';

/** A step is at most this share of the shortest time the pulls take to turn a velocity (sqrt(r^3 / GM) of each body). */
export const STEP_SHARE = 0.03;
/** At most this many steps a frame: past that the steps lengthen (a stalled tab, a very high tick rate) rather than the frame. */
export const MAX_STEPS = 400;
/** The most ticks one frame advances: a longer gap (a hidden tab) is cut, and the observer and the bodies part company by what is cut. */
export const MAX_TICKS_PER_FRAME = 5000;

export class Orbiter {
  /** The star's frame, metres; and m per tick. */
  pos: V3;
  vel: V3;
  /** The tick the state is at. */
  tick: number;

  constructor(pos: V3, vel: V3, tick: number) { this.pos = pos; this.vel = vel; this.tick = tick; }

  /**
   * Advance to `tickTo` under gravity and `thrust` (m per tick^2, star-frame axes, held). `floor[i]` is the closest the centre of body `i` may be approached (m, 0 for no limit): at it the
   * observer slides (the velocity into the body, relative to the body, is taken off) and does not go in.
   */
  advance(B: Bodies, tickTo: number, thrust: V3, floor: ArrayLike<number>): void {
    let remaining = Math.min(tickTo - this.tick, MAX_TICKS_PER_FRAME);
    if (tickTo - this.tick > MAX_TICKS_PER_FRAME) this.tick = tickTo - MAX_TICKS_PER_FRAME;
    if (!(remaining > 0)) { this.tick = Math.max(this.tick, tickTo); return; }
    const a0: V3 = [0, 0, 0], a1: V3 = [0, 0, 0];
    let guard = 0;
    while (remaining > 1e-9 && guard++ < MAX_STEPS * 4) {
      let abs = B.absAll(this.tick);
      // the shortest turning time among the pulls that matter
      let tmin = Infinity, amax = 0;
      for (let i = 0; i < B.list.length; i++) {
        const b = B.list[i];
        if (!b.gm) continue;
        const d = Math.max(len(sub(abs[i], this.pos)), b.radius, 1);
        amax = Math.max(amax, b.gm / (d * d));
      }
      for (let i = 0; i < B.list.length; i++) {
        const b = B.list[i];
        if (!b.gm) continue;
        const d = Math.max(len(sub(abs[i], this.pos)), b.radius, 1);
        if (b.gm / (d * d) > 1e-3 * amax) tmin = Math.min(tmin, Math.sqrt(d * d * d / b.gm));
      }
      const dt = Math.min(remaining, Math.max(STEP_SHARE * tmin, remaining / MAX_STEPS));
      pullAt(B, abs, this.pos, a0);
      this.vel = [this.vel[0] + (a0[0] + thrust[0]) * 0.5 * dt, this.vel[1] + (a0[1] + thrust[1]) * 0.5 * dt, this.vel[2] + (a0[2] + thrust[2]) * 0.5 * dt];
      this.pos = [this.pos[0] + this.vel[0] * dt, this.pos[1] + this.vel[1] * dt, this.pos[2] + this.vel[2] * dt];
      this.tick += dt; remaining -= dt;
      abs = B.absAll(this.tick);
      pullAt(B, abs, this.pos, a1);
      this.vel = [this.vel[0] + (a1[0] + thrust[0]) * 0.5 * dt, this.vel[1] + (a1[1] + thrust[1]) * 0.5 * dt, this.vel[2] + (a1[2] + thrust[2]) * 0.5 * dt];
      this.slide(B, abs, floor);
    }
    this.tick = Math.max(this.tick, tickTo);
  }

  /** Keep the observer out of the shells `floor` describes, taking the inward part of its velocity (relative to the body) off. */
  private slide(B: Bodies, abs: V3[], floor: ArrayLike<number>): void {
    for (let i = 0; i < B.list.length; i++) {
      const f = floor[i];
      if (!(f > 0)) continue;
      const d = sub(this.pos, abs[i]), r = len(d);
      if (r >= f) continue;
      const n: V3 = r > 0 ? scale(d, 1 / r) : norm([0, 0, 1]);
      this.pos = add(abs[i], scale(n, f));
      const vb = B.absVel(i, this.tick);
      const vn = dot(sub(this.vel, vb), n);
      if (vn < 0) this.vel = sub(this.vel, scale(n, vn));
    }
  }
}
